import React, { ReactElement, useContext, useState } from 'react';
import Button from '../../widget-ui-toolkit/Button';
import { SizeContext } from '../../utils/SizeContext';
import { CalendarViewProps, MainSubViews, MainViewProps } from './types';
import formatDate from './formatDate';
import FormattedMessage from '../../widget-ui-toolkit/FormattedMessage';
import { ValidationErrors } from '../../utils/validation/types';
import styles from './MainView.module.css';
import { isProdEnvironment } from '../../utils/environment';
import BackButton from '../../widget-ui-toolkit/Button/BackButton';
import ViewTitle from '../../components/ViewTitle';
import SelectPatientTypeView from './PatientTypeSelector/SelectPatientTypeView';
import AdultBookingView from './AdultBooking/AdultBookingView';
import ChildBookingView from './ChildBooking/ChildBookingView';
import BookingSearchTextInput from '../../widget-ui-toolkit/BookingTextInput/BookingSearchTextInput';
import CarerView from './Carer/CarerView';

const LOCALIZE_PREFIX = 'widget.booking.main';

const MainView = ({
  booking,
  onChangeBooking,
  onSelectNewAppointmentDateTime,
  appointmentDateTime,
  onBookAppointment,
  isSlotUnavailable,
  partnership,
  patientTypeOption,
  setPatientTypeOption,
  showNurse,
}: MainViewProps): ReactElement => {
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const onSizeUpdate = useContext(SizeContext);
  const [subView, setSubView] = useState<MainSubViews>(MainSubViews.Booking);

  function handleChecked() {
    onChangeBooking({ ...booking, optionChecked: !booking.optionChecked });
    onSizeUpdate();
  }

  function handleOptionChanged() {
    onChangeBooking({
      ...booking,
      optionChecked: true,
      isChild: !booking.isChild,
    });
    onSizeUpdate();
  }

  const submitBooking = () => {
    onBookAppointment();
  };

  function getCalendarViewProps() {
    const calendarViewProps: CalendarViewProps = {};
    calendarViewProps.nhsPatientSelectorEnabled = true;

    if (booking.optionChecked && !booking.isChild) {
      calendarViewProps.nhsAdultPatientViewEnabled = true;
    }
    if (booking.optionChecked && booking.isChild) {
      calendarViewProps.nhsChildPatientViewEnabled = true;
    }

    return calendarViewProps;
  }

  return (
    <>
      <div className={styles.appointmentDateTime}>
        <BookingSearchTextInput
          onClick={onSelectNewAppointmentDateTime}
          validationError={
            isSlotUnavailable ? ValidationErrors.SlotUnavailable : undefined
          }
          defaultValue={formatDate(appointmentDateTime)}
          id="appointment"
          imageAltText="Select date and time for appointment"
          careType={showNurse ? 'Nurse' : 'GP'}
        />
      </div>
      {subView === MainSubViews.Booking && (
        <>
          {getCalendarViewProps().nhsPatientSelectorEnabled && (
            <SelectPatientTypeView
              patientTypeOption={patientTypeOption}
              setPatientTypeOption={(patientOption) => {
                setPatientTypeOption(patientOption);
                handleOptionChanged();
              }}
            />
          )}
          {getCalendarViewProps().nhsAdultPatientViewEnabled && (
            <AdultBookingView
              booking={booking}
              onChangeBooking={onChangeBooking}
              setValidationState={setIsFormValid}
            />
          )}
          {getCalendarViewProps().nhsChildPatientViewEnabled && (
            <ChildBookingView
              booking={booking}
              onChangeBooking={onChangeBooking}
              setValidationState={setIsFormValid}
            />
          )}
        </>
      )}
      {subView === MainSubViews.Booking && (
        <Button
          disabled={!isFormValid || isSlotUnavailable}
          onClick={submitBooking}
        >
          <FormattedMessage id={`${LOCALIZE_PREFIX}.book_appointment_button`} />
        </Button>
      )}
    </>
  );
};

export default MainView;
